import { API_ENDPOINTS } from '../../config/apiEndpoints.js';
import { ApiService } from './ApiService.js';

// Export ApiService class for external use
export { ApiService };
import {
  User,
  UserUpdateInput,
  Consultation,
  ConsultationCreateInput,
  ConsultationUpdateInput,
  ConsultationSearchParams,
  Appointment,
  AppointmentCreateInput,
  AppointmentUpdateInput,
  AppointmentRescheduleInput,
  AppointmentSearchParams,
  Message,
  MessageCreateInput,
  VideoCall,
  VideoCallCreateInput,
  UserSearchParams
} from '../../types/api.js';

import authService from '../authService.js'; // Assuming default export
import { logger } from '../../utils/logger.js';

// Instance unique du service API
const api = ApiService.getInstance();

// Export the API instance for direct HTTP method access
export { api };

// Re-export the canonical authService
export { authService };

// API utilities for monitoring and error handling
export const apiUtils = {
  getHealthStatus: async () => {
    return api.get(API_ENDPOINTS.HEALTH);
  },
  getMetrics: async () => {
    return api.get(API_ENDPOINTS.SYSTEM.METRICS);
  },
  healthCheck: async () => {
    return api.get(API_ENDPOINTS.HEALTH);
  },
  getApiInfo: async () => {
    return api.get(API_ENDPOINTS.SYSTEM.INFO);
  }
};

// Setup API error toasts
export const setupApiErrorToasts = () => {
  // This function can be used to setup global error handling for API calls
  logger.debug('API error toasts setup');
};

// Service de consultation
export const consultationService = {
  create: async (data: ConsultationCreateInput): Promise<Consultation> => {
    return api.post<Consultation>(API_ENDPOINTS.MEDICAL.CONSULTATIONS.BASE, data);
  },
  update: async (id: string, data: ConsultationUpdateInput): Promise<Consultation> => {
    return api.put<Consultation>(`${API_ENDPOINTS.MEDICAL.CONSULTATIONS.BASE}/${id}`, data);
  },
  get: async (id: string): Promise<Consultation> => {
    return api.get<Consultation>(`${API_ENDPOINTS.MEDICAL.CONSULTATIONS.BASE}/${id}`);
  },
  list: async (params?: ConsultationSearchParams): Promise<Consultation[]> => {
    return api.get<Consultation[]>(API_ENDPOINTS.MEDICAL.CONSULTATIONS.BASE, { params });
  },
  delete: async (id: string): Promise<void> => {
    return api.delete<void>(`${API_ENDPOINTS.MEDICAL.CONSULTATIONS.BASE}/${id}`);
  }
};

// Service de rendez-vous
export const appointmentService = {
  create: async (data: AppointmentCreateInput): Promise<Appointment> => {
    return api.post<Appointment>(API_ENDPOINTS.APPOINTMENTS.BASE, data);
  },
  update: async (id: string, data: AppointmentUpdateInput): Promise<Appointment> => {
    return api.put<Appointment>(`${API_ENDPOINTS.APPOINTMENTS.BASE}/${id}`, data);
  },
  get: async (id: string): Promise<Appointment> => {
    return api.get<Appointment>(`${API_ENDPOINTS.APPOINTMENTS.BASE}/${id}`);
  },
  list: async (params?: AppointmentSearchParams): Promise<Appointment[]> => {
    return api.get<Appointment[]>(API_ENDPOINTS.APPOINTMENTS.BASE, { params });
  },
  cancel: async (id: string): Promise<Appointment> => {
    return api.post<Appointment>(`${API_ENDPOINTS.APPOINTMENTS.BASE}/${id}/cancel`);
  },
  reschedule: async (id: string, data: AppointmentRescheduleInput): Promise<Appointment> => {
    return api.post<Appointment>(`${API_ENDPOINTS.APPOINTMENTS.BASE}/${id}/reschedule`, data);
  }
};

// Service de messages
export const messageService = {
  send: async (data: MessageCreateInput): Promise<Message> => {
    return api.post<Message>(API_ENDPOINTS.MESSAGING.BASE, data);
  },
  getConversation: async (conversationId: string): Promise<Message[]> => {
    return api.get<Message[]>(`${API_ENDPOINTS.MESSAGING.BASE}/conversation/${conversationId}`);
  },
  getConversations: async (): Promise<Message[]> => {
    return api.get<Message[]>(`${API_ENDPOINTS.MESSAGING.CONVERSATIONS}`);
  },
  markAsRead: async (messageId: string): Promise<void> => {
    return api.post<void>(`${API_ENDPOINTS.MESSAGING.BASE}/${messageId}/read`);
  },
  delete: async (messageId: string): Promise<void> => {
    return api.delete<void>(`${API_ENDPOINTS.MESSAGING.BASE}/${messageId}`);
  }
};

// Service d'appels vidéo
export const videoCallService = {
  initiate: async (data: VideoCallCreateInput): Promise<VideoCall> => {
    return api.post<VideoCall>(API_ENDPOINTS.VIDEO_CALLS.BASE, data);
  },
  getById: async (callId: number): Promise<{ data: VideoCall }> => {
    const result = await api.get<VideoCall>(`${API_ENDPOINTS.VIDEO_CALLS.BASE}/${callId}`);
    return { data: result };
  },
  join: async (callId: string): Promise<VideoCall> => {
    return api.post<VideoCall>(`${API_ENDPOINTS.VIDEO_CALLS.BASE}/${callId}/join`);
  },
  leave: async (callId: string): Promise<VideoCall> => {
    return api.post<VideoCall>(`${API_ENDPOINTS.VIDEO_CALLS.BASE}/${callId}/leave`);
  },
  end: async (callId: string): Promise<VideoCall> => {
    return api.post<VideoCall>(`${API_ENDPOINTS.VIDEO_CALLS.BASE}/${callId}/end`);
  },
  cancel: async (callId: number): Promise<VideoCall> => {
    return api.post<VideoCall>(`${API_ENDPOINTS.VIDEO_CALLS.BASE}/${callId}/cancel`);
  },
  getToken: async (callId: string): Promise<{ token: string }> => {
    return api.get<{ token: string }>(`${API_ENDPOINTS.VIDEO_CALLS.BASE}/${callId}/token`);
  }
};

// Service utilisateur
export const userService = {
  get: async (id: string): Promise<User> => {
    const response = await api.get<{ status: string; data: User }>(`${API_ENDPOINTS.USERS.BASE}/${id}`);
    // ✅ Extraire depuis response.data si la réponse est wrappée
    if (response && typeof response === 'object' && 'data' in response) {
      return response.data;
    }
    return response as unknown as User;
  },
  getUserById: async (id: string | number): Promise<User> => {
    const response = await api.get<{ status: string; data: User }>(`${API_ENDPOINTS.USERS.BASE}/${id}`);
    // ✅ Extraire depuis response.data si la réponse est wrappée
    if (response && typeof response === 'object' && 'data' in response) {
      return response.data;
    }
    return response as unknown as User;
  },
  update: async (id: string, data: UserUpdateInput): Promise<User> => {
    return api.put<User>(`${API_ENDPOINTS.USERS.BASE}/${id}`, data);
  },
  list: async (params?: UserSearchParams): Promise<User[]> => {
    return api.get<User[]>(API_ENDPOINTS.USERS.BASE, { params });
  },
  getAllUsers: async (): Promise<User[]> => {
    const response = await api.get<{ status: string; data: User[]; pagination?: unknown }>(API_ENDPOINTS.USERS.BASE);
    // Backend retourne { status: 'success', data: User[], pagination: {...} }
    // Extraire le tableau data si présent, sinon retourner la réponse directement
    if (response && typeof response === 'object' && 'data' in response && Array.isArray(response.data)) {
      return response.data;
    }
    // Fallback: si la réponse est déjà un tableau, le retourner directement
    if (Array.isArray(response)) {
      return response;
    }
    // Si aucune structure attendue, retourner un tableau vide
    return [];
  },
  getUsersByRole: async (role: string): Promise<User[]> => {
    const response = await api.get<{ status?: string; data?: User[]; pagination?: unknown } | User[]>(
      API_ENDPOINTS.USERS.BASE,
      { params: { role } }
    );
    if (Array.isArray(response)) {
      return response;
    }
    if (response && typeof response === 'object' && 'data' in response && Array.isArray(response.data)) {
      return response.data;
    }
    return [];
  },
  getDoctors: async (): Promise<User[]> => {
    const response = await api.get<{ status?: string; data?: User[]; pagination?: unknown } | User[]>(
      API_ENDPOINTS.USERS.BASE,
      { params: { role: 'DOCTOR' } }
    );
    if (Array.isArray(response)) {
      return response;
    }
    if (response && typeof response === 'object' && 'data' in response && Array.isArray(response.data)) {
      return response.data;
    }
    return [];
  },
  getUserStats: async (_filter?: string): Promise<UserStatsResponse> => {
    return api.get<UserStatsResponse>(`${API_ENDPOINTS.USERS.BASE}/stats`);
  },
  search: async (query: string): Promise<User[]> => {
    return api.get<User[]>(`${API_ENDPOINTS.USERS.SEARCH}?q=${encodeURIComponent(query)}`);
  },
  delete: async (id: string): Promise<void> => {
    return api.delete<void>(`${API_ENDPOINTS.USERS.BASE}/${id}`);
  },
  deleteUser: async (id: string): Promise<void> => {
    return api.delete<void>(`${API_ENDPOINTS.USERS.BASE}/${id}`);
  }
};

// Export des services
const services = {
  auth: authService,
  consultation: consultationService,
  appointment: appointmentService,
  message: messageService,
  videoCall: videoCallService,
  user: userService,
  patch: async <TResponse = unknown, TData = Record<string, unknown>>(
    endpoint: string,
    data: TData
  ): Promise<TResponse> => {
    return api.patch<TResponse>(endpoint, data);
  }
};

export default services;

interface UserStatsResponse {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  doctors: number;
  patients: number;
  admins: number;
}
